'use client';

import { Fragment, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';

import { cn } from '@/utils/uiUtils';

interface DropdownProps {
  children: ReactNode;
  trigger: ReactNode;
  placement?:
    | 'bottom-start'
    | 'bottom'
    | 'bottom-end'
    | 'top-start'
    | 'top'
    | 'top-end';
  disabled?: boolean;
  className?: string;
  menuClassName?: string;
  matchTriggerWidth?: boolean;
}

export function Dropdown({
  children,
  trigger,
  placement = 'bottom-start',
  disabled = false,
  className,
  menuClassName,
  matchTriggerWidth = true,
}: DropdownProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  const updateWidth = () => {
    if (menuRef.current) {
      // Find the first child element (the trigger button)
      const firstChild = menuRef.current.firstElementChild as HTMLElement;
      if (firstChild) {
        setWidth(firstChild.offsetWidth);
      }
    }
  };

  useLayoutEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <Menu as='div' ref={menuRef} className={cn('relative', className)}>
      <Menu.Button as={Fragment} disabled={disabled}>
        {trigger}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'
      >
        <Menu.Items
          style={
            matchTriggerWidth
              ? { width: width ? `${width}px` : undefined }
              : undefined
          }
          className={cn(
            'absolute z-50 mt-2 origin-top-left max-h-72 overflow-y-auto rounded-lg border border-stone-300 bg-white text-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:border-stone-700 dark:bg-stone-700 dark:text-gray-100 calendar-scrollbar',
            !matchTriggerWidth && 'w-max min-w-full',
            menuClassName,
            placement === 'bottom-start' && 'left-0',
            placement === 'bottom' && 'left-1/2 -translate-x-1/2',
            placement === 'bottom-end' && 'right-0',
            placement === 'top-start' && 'bottom-full mb-2 left-0',
            placement === 'top' && 'bottom-full mb-2 left-1/2 -translate-x-1/2',
            placement === 'top-end' && 'bottom-full mb-2 right-0'
          )}
        >
          <div className='py-1'>{children}</div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

interface DropdownItemProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  active?: boolean;
}

Dropdown.Item = function DropdownItem({
  children,
  onClick,
  className,
  active = false,
}: DropdownItemProps) {
  return (
    <Menu.Item>
      {({ active: isActive }) => (
        <button
          type='button'
          onClick={onClick}
          className={cn(
            'flex w-full cursor-pointer items-center justify-start px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-stone-100 dark:hover:bg-stone-600/70 focus:bg-stone-100 dark:focus:bg-stone-600/70 focus:outline-none',
            className,
            (active || isActive) &&
              'font-medium bg-stone-100 text-gray-900 dark:bg-stone-600/70 dark:text-gray-100'
          )}
        >
          {children}
        </button>
      )}
    </Menu.Item>
  );
};
